import { useMemo } from 'react';

import {
  isDisabledConfig,
  useEditorState,
} from './context/VideoEditorStateContext';
import { useTemplateState } from './context/VideoTemplateStateContext';

export interface ImageInstanceLock {
  disabled: true;
  tooltip?: string;
}

/**
 * Resolves the all-or-nothing instance lock for the slideshow slide
 * identified by `slideId`. Returns `undefined` when the slide isn't playing
 * the `mainImage` role or when no relevant lockSpec entry applies.
 *
 * Granular per-property locks (remove vs image vs style vs position vs size)
 * intentionally collapse into a single flag at the editor layer — see
 * SPAR-27811 for the rationale. `mainImage` is the only addressable slot
 * today; generalize the role lookup here when additional named slots are
 * introduced.
 */
export default function useImageLockedFeatures(
  slideId: string,
): ImageInstanceLock | undefined {
  const { features } = useEditorState();
  const { mainImage } = useTemplateState();

  return useMemo(() => {
    const isMainImage = mainImage?.ids.includes(slideId) ?? false;
    if (!isMainImage) {
      return undefined;
    }

    const slideshowImage = features?.slideshowImage;

    if (slideshowImage === 'locked') {
      return { disabled: true };
    }

    if (typeof slideshowImage !== 'object') {
      return undefined;
    }

    const slotState = slideshowImage.mainImage;
    if (!isDisabledConfig(slotState)) {
      return undefined;
    }

    return { disabled: true, tooltip: slotState.reason };
  }, [features, mainImage, slideId]);
}
